import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreateCompanyDto, UserProfileDto, UserRole } from "@green0meter/api-client";
import { FieldValues, UseFormSetError } from "react-hook-form";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { DOMAIN_COMPANIES } from "../domain";
import { ACTION_GET_COMPANIES } from "../GetCompanies";
import { ACTION_GET_ASSIGNED_COMPANIES } from "../GetAssignedCompanies/useGetAssignedCompanies";

import { companiesApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_CREATE_COMPANY = "CREATE_COMPANY";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

const createCompany = async (params: CreateCompanyDto): Promise<void> => {
  await companiesApi.companiesControllerCreateCompany(params as CreateCompanyDto);
};

export const useCreateCompany = (
  user: UserProfileDto | null,
  setError?: UseFormSetError<FieldValues>,
): {
  createCompany: (params: CreateCompanyDto) => void;
  createCompanyEnabled: boolean;
  isSuccessCreateCompany: boolean;
  isPendingCreateCompany: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_COMPANIES, ACTION_CREATE_COMPANY],
    mutationFn: enabled ? createCompany : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_COMPANIES],
      });
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_ASSIGNED_COMPANIES],
      });
    },
    onError: (error: AxiosError) => {
      if (setError) {
        const apiError = error as {
          response?: { data?: { code?: string; message?: string } };
        };

        const code = apiError?.response?.data?.code;

        if (code === "company_already_exists") {
          setError("vat", {
            message: t(`organization.errors.${code}`),
            type: "server",
          });
        }
      }
    },
  });

  return {
    createCompany: mutate,
    createCompanyEnabled: enabled,
    isSuccessCreateCompany: isSuccess,
    isPendingCreateCompany: isPending,
  };
};
